import React, { useEffect, useState } from 'react';
import { ISbRichtext, renderRichText, SbGatsbyStory } from 'gatsby-source-storyblok';
import { IStoryblokImageAsset } from '../types';
import ArticlePrimarySection from '../../containers/sections/article-primary-section/article-primary-section';
import ArticleTextSection from '../../containers/sections/article-text-section/article-text-section';

export interface IArticle {
  article_content: ISbRichtext;
  category: string;
  title: string;
  time_to_read: string;
  short_description: string;
  primary_image: IStoryblokImageAsset;
  full_slug: string;
  created_at: string;

  author_name: string;
  author_caption: string;
  author_position: string;
  author_linkedin_link: string;
  author_img: IStoryblokImageAsset;
  testimonial: string[];
}

export function ArticlePageStoryblokComponent({ blok, rels }: { blok: IArticle; rels: SbGatsbyStory[] }) {
  const testimonial = rels.find((rel) => rel.uuid === blok.testimonial?.[0])?.content;

  const breadcrumbs = [
    { text: 'Company', link: '/' },
    { text: 'Blog', link: `/${blok.full_slug?.split('/')[0]}` },
    { text: blok.title },
  ];

  const isAuthorDefined =
    blok.author_name && blok.author_caption && blok.author_position && blok.author_linkedin_link && blok.author_img;

  const articleHtml = renderRichText(blok.article_content);

  const [anchors, setAnchors] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(articleHtml, 'text/html');
    const anchorsArray = Array.from(doc.querySelectorAll('[id]')).map((anchor) => ({
      id: anchor.id,
      title: anchor.textContent || '',
    }));
    setAnchors(anchorsArray);
  }, [blok.article_content]);

  return (
    <>
      <ArticlePrimarySection
        breadcrumbs={breadcrumbs}
        category={blok.category}
        description={blok.short_description}
        title={blok.title}
        image={{ src: blok.primary_image.filename, alt: blok.primary_image.alt }}
        timeToRead={blok.time_to_read}
        articleAnchors={anchors}
        createdAt={blok.created_at}
        testimonial={
          testimonial
            ? {
                avatarImgSrc: testimonial.avatar.filename,
                text: testimonial.text,
                location: testimonial.location,
                name: testimonial.name,
                position: testimonial.position,
                rate: testimonial.rate,
              }
            : undefined
        }
      />

      <ArticleTextSection
        articleHtml={articleHtml}
        articleAnchors={anchors}
        author={
          isAuthorDefined
            ? {
                name: blok.author_name,
                caption: blok.author_caption,
                position: blok.author_position,
                image: { src: blok.author_img?.filename, alt: blok.author_img?.alt },
                linkedinLink: blok.author_linkedin_link,
              }
            : undefined
        }
        testimonial={
          testimonial
            ? {
                avatarImgSrc: testimonial.avatar.filename,
                text: testimonial.text,
                location: testimonial.location,
                name: testimonial.name,
                position: testimonial.position,
                rate: testimonial.rate,
              }
            : undefined
        }
      />
    </>
  );
}
